/**
 * GlowGhost Engine model definitions
 * ==================================
 * Local models are STATIC IDENTITY + DISCOVERED PRESENCE:
 *
 *   - Identity (id, label, capabilities, licence) is curated and lives here,
 *     so the registry stays a closed, validated set.
 *   - Presence ("is it installed on THIS machine") is owned exclusively by the
 *     Engine and polled live. It is never persisted app-side — a cached
 *     "installed" flag is the named rot point for this design.
 *
 * Every entry is `activation.defaultActive: false`, so local models are
 * invisible until the user turns them on. That rides the existing, tested
 * activation machinery in src/services/models/repository.js.
 *
 * Model ids are deliberately NOT shared with their cloud counterparts. A local
 * model has different timing, different capability limits, and a different
 * failure surface, so it is a distinct entry even where the weights match.
 */

import {
  ModelFamily,
  ModelRouteAdapter,
  ModelSurface,
  ModelTopology,
} from '../../config/models/schema.js';

/**
 * Licence access, driving REQUIRED STEPS — never permission.
 *   'open'   — ungated: downloads immediately, no account.
 *   'gated'  — the weights host requires an accepted agreement and a token.
 * There is deliberately no "blocked" value. Whether a user may use a model is
 * the user's decision and the weights host's gate, not the app's.
 */
export const LocalModelAccess = Object.freeze({
  OPEN: 'open',
  GATED: 'gated',
});

/**
 * Fallback timing only.
 *
 * The Engine reports real phase and step progress, so local jobs are driven by
 * measured state, not by this number (see engineImageService.describeEngineProgress).
 * This exists because the registry schema requires positive `baseSeconds`. If
 * this value is ever visibly driving a local loader, that is a bug to surface,
 * not a number to tune.
 */
const ENGINE_FALLBACK_TIMING = {
  source: 'engine-fallback-only',
  base: 30,
  baseSeconds: 30,
};

export const FLUX1_SCHNELL_LOCAL_MODEL_DEFINITION = {
  id: 'flux1SchnellLocal',
  label: 'FLUX.1 schnell (local)',
  family: ModelFamily.IMAGE,
  provider: 'glowghostEngine',
  // Distinct tier per model — see the note on sdxlLocal.
  qualityTier: 'flux-schnell-local',
  iconKey: 'flux',
  // Local generation costs nothing per image. The cost is disk and time. A $0 price, not
  // null: null would report an unknown price for a model that is free. Its label derives
  // as "Free — runs on your machine".
  pricing: { unit: 'image', usd: 0, asOf: '2026-10-20', note: 'runs on your machine' },
  timing: ENGINE_FALLBACK_TIMING,
  surfaces: [ModelSurface.IMAGE_GENERATE],
  topologies: [ModelTopology.TEXT_TO_IMAGE],
  // Off until the user enables it. Riding existing activation machinery means
  // absence needs no special-casing anywhere else.
  activation: { defaultActive: false },
  local: {
    // MEASURED 2026-08-09, and it corrected the plan. The weights are
    // Apache-2.0, but `black-forest-labs/FLUX.1-schnell` is gated:auto on
    // Hugging Face and an anonymous fetch of `ae.safetensors` returns
    // "Access denied. This repository requires approval."
    //
    // The UNet and text encoders have ungated mirrors from trustworthy
    // publishers; the VAE does not, and unvetted community re-uploads are a
    // supply-chain decision rather than a convenience. ONE gated artifact
    // makes the whole model gated.
    //
    // Consequence: this is NOT the zero-friction first-run default it was
    // specced as. A licence badge is not a gating check — only a fetch is.
    access: LocalModelAccess.GATED,
    licenceSummary: 'Apache 2.0 — free for any use. Needs a free HuggingFace account to download.',
    // ~31.5 GB total: 22.1 UNet + 9.1 T5 + 0.24 CLIP + 0.34 VAE. The 6.9 GB
    // figure in the original plan was wrong — it described neither this
    // packaging nor these artifacts.
    downloadBytes: 31.5 * 1024 ** 3,
    repo: 'black-forest-labs/FLUX.1-schnell',
  },
  features: {
    aspectRatio: {
      options: ['1:1', '3:4', '4:3', '2:3', '3:2', '9:16', '16:9'],
      default: '1:1',
    },
    batch: { min: 1, max: 4 },
    promptImages: { generateMax: 0, editMax: 0 },
    negativePrompt: { enabled: false },
    guidanceScale: { enabled: false },
    inferenceSteps: { enabled: false },
    resolution: null,
    boardPresets: { enabled: false },
  },
  routes: {
    [ModelTopology.TEXT_TO_IMAGE]: {
      endpoint: 'glowghost-engine:flux1-schnell/text-to-image',
      timing: ENGINE_FALLBACK_TIMING,
      execution: {
        type: 'provider-service-export',
        adapterId: ModelRouteAdapter.IMAGE_TEXT_BASIC,
        providerKey: 'glowghostEngine',
        exportName: 'generateFluxSchnellLocalImage',
        endpoint: 'glowghost-engine:flux1-schnell/text-to-image',
      },
    },
  },
  prompt: { enhancerId: 'flux1-schnell-local' },
  presentation: {
    label: 'FLUX.1 schnell (local)',
    shortLabel: 'FLUX schnell',
    //: Deliberately does NOT say "private". Inference runs locally, but every
    //: result uploads to Supabase to reach the gallery and survive reload, and
    //: opted-in prompt enhancement routes through FAL/OpenRouter. Claiming
    //: privacy we do not provide is worse than claiming nothing; earning the
    //: word needs a local-only persistence mode, which does not exist.
    description: 'Generation runs on your own machine via Modul Engine. Free — results still sync to your library.',
    group: 'Image generation',
    tags: ['local', 'fast'],
  },
  ui: {
    image: {
      supportsProps: false,
      //: Attached images ride the GENERATE request. Without this the view model
      //: defaults to 'edit', the request key flips to the -edit variant, and
      //: `service.edit` is undefined here because no route declares one — so the
      //: first attach threw a TypeError at submit. This declares UI routing
      //: identity only. It asserts no acceptance: the Engine still owns that,
      //: and with no snapshot nothing attaches at all.
      attachedImageMode: 'reference',
    },
  },
};

/**
 * The no-login default.
 *
 * Chosen for access, not for benchmark scores. Every artifact is one 6.9 GB
 * file that fetches anonymously (measured: HTTP 206), so there is no second
 * artifact that could quietly be gated the way FLUX's VAE is. No fp8 anywhere,
 * so it clears the Apple Silicon cast limitation too.
 *
 * Verified end to end on an M5/32 GB: 1024x1024 in 137 s, no account.
 */
export const SDXL_LOCAL_MODEL_DEFINITION = {
  id: 'sdxlLocal',
  label: 'Stable Diffusion XL (local)',
  family: ModelFamily.IMAGE,
  provider: 'glowghostEngine',
  // REQUIRED, and its absence is why local generation silently fell back to a
  // cloud model. `IMAGE_MODEL_BY_QUALITY.set(model.qualityTier, model)`
  // (src/config/imageModels.js:282) is how a picker selection resolves back to
  // a model. Without a tier every local model registers under `undefined`,
  // so they overwrite each other AND no selection can ever resolve — the
  // composer silently kept the previous model and generated in the cloud,
  // which looks exactly like success.
  qualityTier: 'sdxl-local',
  iconKey: 'stability',
  pricing: { unit: 'image', usd: 0, asOf: '2026-10-20', note: 'runs on your machine' },
  timing: ENGINE_FALLBACK_TIMING,
  surfaces: [ModelSurface.IMAGE_GENERATE],
  topologies: [ModelTopology.TEXT_TO_IMAGE],
  activation: { defaultActive: false },
  local: {
    access: LocalModelAccess.OPEN,
    licenceSummary: 'Open licence (CreativeML OpenRAIL++). No account needed.',
    downloadBytes: 6.9 * 1024 ** 3,
    repo: 'stabilityai/stable-diffusion-xl-base-1.0',
    isDefault: true,
  },
  features: {
    aspectRatio: {
      options: ['1:1', '3:4', '4:3', '2:3', '3:2', '9:16', '16:9'],
      default: '1:1',
    },
    batch: { min: 1, max: 4 },
    promptImages: { generateMax: 0, editMax: 0 },
    // SDXL is CFG-driven, so unlike schnell a negative prompt does real work.
    negativePrompt: { enabled: true },
    guidanceScale: { enabled: false },
    inferenceSteps: { enabled: false },
    resolution: null,
    boardPresets: { enabled: false },
  },
  routes: {
    [ModelTopology.TEXT_TO_IMAGE]: {
      endpoint: 'glowghost-engine:sdxl/text-to-image',
      timing: ENGINE_FALLBACK_TIMING,
      execution: {
        type: 'provider-service-export',
        adapterId: ModelRouteAdapter.IMAGE_TEXT_BASIC,
        providerKey: 'glowghostEngine',
        // Model-specific export: the request params carry no modelId.
        exportName: 'generateSdxlLocalImage',
        endpoint: 'glowghost-engine:sdxl/text-to-image',
      },
    },
  },
  prompt: { enhancerId: 'sdxl-local' },
  presentation: {
    label: 'Stable Diffusion XL (local)',
    shortLabel: 'SDXL local',
    //: See the note on the other definition: no privacy claim without a
    //: local-only persistence mode to back it.
    description: 'Generation runs on your own machine. Free and needs no account — results still sync to your library.',
    group: 'Image generation',
    tags: ['local', 'no-account'],
  },
  ui: {
    image: {
      supportsProps: false,
      //: Attached images ride the GENERATE request. Without this the view model
      //: defaults to 'edit', the request key flips to the -edit variant, and
      //: `service.edit` is undefined here because no route declares one — so the
      //: first attach threw a TypeError at submit. This declares UI routing
      //: identity only. It asserts no acceptance: the Engine still owns that,
      //: and with no snapshot nothing attaches at all.
      attachedImageMode: 'reference',
    },
  },
};

export const GLOWGHOST_ENGINE_MODEL_DEFINITIONS = [
  SDXL_LOCAL_MODEL_DEFINITION,
  FLUX1_SCHNELL_LOCAL_MODEL_DEFINITION,
];

/**
 * The model a new user gets first. It MUST be usable with no account —
 * enforced by a test, because "just works" dies at a login wall.
 */
export const DEFAULT_LOCAL_IMAGE_MODEL_ID = SDXL_LOCAL_MODEL_DEFINITION.id;
